import { useState } from "react";
import type { PlanSummary } from "@/types";
import { moduleService } from "@/services/module.service";
import {
  PLAN_TYPES,
  type PlanFormData,
  emptyFormData,
} from "../hooks/usePlanManager";

interface PlanTableProps {
  planRows: PlanSummary[];
  isLoading: boolean;
  isUpdating: boolean;
  updatePlan: (args: { id: string; payload: PlanFormData }) => Promise<any>;
}

export function PlanTable({
  planRows,
  isLoading,
  isUpdating,
  updatePlan,
}: PlanTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<PlanFormData>({
    ...emptyFormData,
  });

  const handleEdit = async (plan: PlanSummary) => {
    setEditingId(plan.id);
    let planModuleCodes: string[] = [];
    try {
      const planModules = await moduleService.getPlanModules(plan.id);
      planModuleCodes = planModules.map((m) => m.code);
    } catch {
      // Plan might not have modules yet
    }
    setEditingDraft({
      name: plan.name,
      type: plan.type,
      description: plan.description ?? "",
      priceMonthly: plan.priceMonthly ? Number(plan.priceMonthly) : 0,
      priceYearly: plan.priceYearly ? Number(plan.priceYearly) : 0,
      maxStores: plan.maxStores,
      maxUsers: plan.maxUsers,
      storageGB: plan.storageGB,
      features: plan.features ?? [],
      trialDays: plan.trialDays ?? 0,
      maxOfflineDays: (plan as any).maxOfflineDays ?? 7,
      isActive: plan.isActive,
      selectedModules: planModuleCodes,
    });
  };

  return (
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
        <tr>
          <th className="px-4 py-3">Name</th>
          <th className="px-4 py-3">Type</th>
          <th className="px-4 py-3">Monthly</th>
          <th className="px-4 py-3">Yearly</th>
          <th className="px-4 py-3">Limits</th>
          <th className="px-4 py-3">Status</th>
          <th className="px-4 py-3 text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td className="px-4 py-6 text-sm text-slate-500" colSpan={7}>
              Loading plans...
            </td>
          </tr>
        ) : planRows.length ? (
          planRows.map((plan) => (
            <tr
              key={plan.id}
              className="border-b border-slate-100 last:border-none"
            >
              {editingId === plan.id ? (
                <>
                  <td className="px-4 py-3">
                    <input
                      value={editingDraft.name}
                      onChange={(e) =>
                        setEditingDraft((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="h-8 w-full rounded-lg border border-slate-200 px-2 text-sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={editingDraft.type}
                      onChange={(e) =>
                        setEditingDraft((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm"
                    >
                      {PLAN_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={editingDraft.priceMonthly ?? 0}
                      onChange={(e) =>
                        setEditingDraft((prev) => ({
                          ...prev,
                          priceMonthly: Number(e.target.value),
                        }))
                      }
                      className="h-8 w-full rounded-lg border border-slate-200 px-2 text-sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={editingDraft.priceYearly ?? 0}
                      onChange={(e) =>
                        setEditingDraft((prev) => ({
                          ...prev,
                          priceYearly: Number(e.target.value),
                        }))
                      }
                      className="h-8 w-full rounded-lg border border-slate-200 px-2 text-sm"
                    />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {editingDraft.maxStores} stores · {editingDraft.maxUsers}{" "}
                    users
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={Boolean(editingDraft.isActive)}
                        onChange={(e) =>
                          setEditingDraft((prev) => ({
                            ...prev,
                            isActive: e.target.checked,
                          }))
                        }
                      />
                      Active
                    </label>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      disabled={isUpdating}
                      onClick={() =>
                        updatePlan({ id: plan.id, payload: editingDraft })
                      }
                      className="rounded-lg bg-slate-900 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-white"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="ml-2 rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600"
                    >
                      Cancel
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {plan.name}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{plan.type}</td>
                  <td className="px-4 py-3 text-slate-600">
                    ₹{plan.priceMonthly ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    ₹{plan.priceYearly ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {plan.maxStores} stores · {plan.maxUsers} users ·{" "}
                    {plan.storageGB} GB · {(plan as any).maxOfflineDays ?? 7}d
                    offline
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {plan.isActive ? "Active" : "Inactive"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleEdit(plan)}
                      className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600"
                    >
                      Edit
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))
        ) : (
          <tr>
            <td className="px-4 py-6 text-sm text-slate-500" colSpan={7}>
              No plans found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
